export type ReplyBlock = { kind: 'text'; text: string } | { kind: 'list'; items: string[] };

export interface ReplyParts {
  blocks: ReplyBlock[];
  /** A question the reply ends on, shown apart so the author sees it waits for an answer. */
  question: string | null;
  /** The reply ends on a question, alone or as the last item of a list. */
  asks: boolean;
}

// A list item starts with "- " at a line start or after a space, never inside a word such as "pre-shift".
const ITEM = /(?:^|\s)-\s+/;
// A numbered item is "1. " and so on, never a number such as "4.2 bar".
const NUMBERED = /(?:^|\s)\d+\.\s+/;
const NUMBERED_LINE = /^\d+\.\s+/;

function blocksOf(paragraph: string): ReplyBlock[] {
  const lines = paragraph
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const blocks: ReplyBlock[] = [];
  const pushText = (text: string) => {
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'text') last.text = `${last.text} ${text}`;
    else blocks.push({ kind: 'text', text });
  };
  const pushItems = (items: string[]) => {
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'list') last.items.push(...items);
    else blocks.push({ kind: 'list', items });
  };
  for (const line of lines) {
    if (line.startsWith('- ')) {
      pushItems([line.slice(2).trim()]);
      continue;
    }
    if (NUMBERED_LINE.test(line)) {
      pushItems([line.replace(NUMBERED_LINE, '').trim()]);
      continue;
    }
    // An inline list, as models often write it: "Try these: - one - two" or "Options: 1. one 2. two".
    const colon = line.indexOf(':');
    const rest = colon >= 0 ? line.slice(colon + 1).trimStart() : '';
    const marker = rest.startsWith('- ') ? ITEM : /^1\.\s/.test(rest) ? NUMBERED : null;
    if (marker) {
      pushText(line.slice(0, colon + 1).trim());
      pushItems(
        rest
          .split(marker)
          .map((s) => s.trim())
          .filter(Boolean)
      );
      continue;
    }
    pushText(line);
  }
  return blocks;
}

// Abbreviations whose full stops stay inside a sentence.
const ABBREVIATION = /\b(?:e\.g|i\.e|etc|vs|approx|incl|no)\./gi;
const KEPT_DOT = '\u2024';

/** Whether a reply ends on a question, so the answer box is where the author goes next. */
export function endsOnQuestion(reply: string): boolean {
  return reply.trim().endsWith('?');
}

/** Splits Ari's reply into paragraphs and lists, and pulls out the question it ends on. */
export function replyParts(reply: string): ReplyParts {
  const text = reply.trim();
  let question: string | null = null;
  let body = text;
  if (text.endsWith('?')) {
    // A full stop in an abbreviation such as e.g. does not end a sentence. The kept dot is one
    // character like the full stop, so lengths still line up with the reply.
    const guarded = text.replace(ABBREVIATION, (abbreviation) => abbreviation.replace(/\./g, KEPT_DOT));
    const sentences = (guarded.match(/[^.!?\n]+[.!?]+/g) ?? [guarded]).map((part) => part.split(KEPT_DOT).join('.'));
    const last = sentences[sentences.length - 1].trim();
    const before = text.slice(0, text.length - sentences[sentences.length - 1].length).trim();
    // A question that is the last item of a list stays in the list.
    const inList = last.startsWith('-') || /(?:^|\s)\d+\.$/.test(before) || /(?:^|\s)-$/.test(before);
    if (!inList) {
      question = last;
      body = before;
    }
  }
  const blocks = body.split(/\n\s*\n/).flatMap((p) => blocksOf(p));
  return { blocks, question, asks: endsOnQuestion(text) };
}

export interface Span {
  text: string;
  bold: boolean;
}

/** Splits text on **bold** markers, which Ari sometimes writes. */
export function boldSpans(text: string): Span[] {
  return text
    .split(/\*\*(.+?)\*\*/)
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter((span) => span.text);
}
